import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskClassificationAssignment } from '../../hooks/useRiskClassificationAssignment';
import { useRiskRationaleOptions } from '../../hooks/useRiskRationaleOptions';
import {
  PC_VALUES,
  FA_VALUES,
  S_VALUES,
  RISK_CLASSIFICATION_FREQUENCY_BUCKETS
} from '../../utils/riskClassificationConstants';
import './RiskClassificationAssignmentPanel.css';

const RATIONALE_SEPARATOR = '; ';

function parseRationaleValue(value, optionLabels) {
  if (!value || !value.trim()) return { selected: [], other: '' };
  const parts = value.split(RATIONALE_SEPARATOR).map((s) => s.trim()).filter(Boolean);
  const optionSet = new Set(optionLabels || []);
  const selected = parts.filter((p) => optionSet.has(p));
  const other = parts.filter((p) => !optionSet.has(p)).join(RATIONALE_SEPARATOR);
  return { selected, other };
}

function serializeRationale(selected, other) {
  const list = [...selected];
  if ((other || '').trim()) list.push(other.trim());
  return list.filter(Boolean).join(RATIONALE_SEPARATOR);
}

/**
 * Risk classification (ORDIN 1761) assignment for one entity (department, room, bed, equipment).
 * Shows effective level (explicit or inherited), or "Not configured" / "Inherited — not reviewed".
 */
const RiskClassificationAssignmentPanel = ({
  organizationId,
  entityType,
  entityId,
  structure,
  item,
  userId,
  useModal = false
}) => {
  const { t } = useTranslation('riskClassification');
  const {
    assignment,
    effective,
    loading,
    error,
    save,
    remove,
    refetch
  } = useRiskClassificationAssignment(organizationId, entityType, entityId, structure, item);
  const { items: rationaleOptions } = useRiskRationaleOptions(organizationId, userId);

  const [editing, setEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    pc: 2,
    fa: 1,
    s: 0,
    riskAssessmentRationale: '',
    autocontrolFrequencyCode: null,
    additionalMitigationCommitments: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [rationaleDropdownOpen, setRationaleDropdownOpen] = useState(false);
  const rationaleDropdownRef = useRef(null);

  useEffect(() => {
    if (!rationaleDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (rationaleDropdownRef.current && !rationaleDropdownRef.current.contains(e.target)) {
        setRationaleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [rationaleDropdownOpen]);

  const startEdit = () => {
    if (assignment) {
      setForm({
        pc: assignment.pc ?? 2,
        fa: assignment.fa ?? 1,
        s: assignment.s ?? 0,
        riskAssessmentRationale: assignment.riskAssessmentRationale ?? '',
        autocontrolFrequencyCode: assignment.autocontrolFrequencyCode ?? null,
        additionalMitigationCommitments: assignment.additionalMitigationCommitments ?? ''
      });
    } else {
      setForm({
        pc: 2,
        fa: 1,
        s: 0,
        riskAssessmentRationale: '',
        autocontrolFrequencyCode: null,
        additionalMitigationCommitments: ''
      });
    }
    setEditing(true);
    if (useModal) setModalOpen(true);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    if (useModal) setModalOpen(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!userId) return;
    const rationale = (form.riskAssessmentRationale || '').trim();
    const hasSelection = rationale.split(RATIONALE_SEPARATOR).some((s) => s.trim());
    if (!hasSelection) {
      setSaveError(t('riskAssessmentRationale') + ' is required (select at least one criterion or enter other)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await save(
        {
          pc: form.pc,
          fa: form.fa,
          s: form.s,
          riskAssessmentRationale: rationale,
          autocontrolFrequencyCode: form.autocontrolFrequencyCode || null,
          additionalMitigationCommitments: (form.additionalMitigationCommitments || '').trim() || null
        },
        userId
      );
      setEditing(false);
      if (useModal) setModalOpen(false);
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const label = (labelKey) => {
    const short = typeof labelKey === 'string' && labelKey.includes('.') ? labelKey.split('.').pop() : labelKey;
    return t(short);
  };

  if (!organizationId) {
    return (
      <div className="rc-assignment-panel rca-no-org">
        <p className="rca-muted">{t('noNomenclature')}</p>
        <p className="rca-muted small">Select an organization to configure risk classification.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rc-assignment-panel rca-loading">
        <span className="rca-spinner" aria-hidden /> {t('loading') || 'Loading…'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rc-assignment-panel rca-error">
        <p>{error}</p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={refetch}>Retry</button>
      </div>
    );
  }

  const formContent = (
    <div className="rca-form">
          <div className="rca-form-row">
            <label>{t('pc')}</label>
            <select
              value={form.pc}
              onChange={(e) => setForm((f) => ({ ...f, pc: Number(e.target.value) }))}
              className="rca-select"
            >
              {PC_VALUES.map((v) => (
                <option key={v.value} value={v.value}>{label(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="rca-form-row">
            <label>{t('fa')}</label>
            <select
              value={form.fa}
              onChange={(e) => setForm((f) => ({ ...f, fa: Number(e.target.value) }))}
              className="rca-select"
            >
              {FA_VALUES.map((v) => (
                <option key={v.value} value={v.value}>{label(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="rca-form-row">
            <label>{t('s')}</label>
            <select
              value={form.s}
              onChange={(e) => setForm((f) => ({ ...f, s: Number(e.target.value) }))}
              className="rca-select"
            >
              {S_VALUES.map((v) => (
                <option key={v.value} value={v.value}>{label(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="rca-form-row">
            <label>{t('riskAssessmentRationale')} *</label>
            {rationaleOptions.length > 0 ? (
              <>
                <div className="rca-rationale-dropdown-wrap" ref={rationaleDropdownRef}>
                  <button
                    type="button"
                    className="rca-rationale-trigger"
                    onClick={() => setRationaleDropdownOpen((o) => !o)}
                    aria-expanded={rationaleDropdownOpen}
                    aria-haspopup="listbox"
                    aria-label={t('riskAssessmentRationale')}
                  >
                    <span className="rca-rationale-tags">
                      {(() => {
                        const { selected } = parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label));
                        if (selected.length === 0) {
                          return <span className="rca-rationale-placeholder">{t('rationalePlaceholder')}</span>;
                        }
                        return selected.map((label) => (
                          <span key={label} className="rca-rationale-tag">
                            {label}
                            <button
                              type="button"
                              className="rca-rationale-tag-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                const { selected: sel, other } = parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label));
                                setForm((f) => ({ ...f, riskAssessmentRationale: serializeRationale(sel.filter((l) => l !== label), other) }));
                              }}
                              aria-label={`Remove ${label}`}
                            >
                              ×
                            </button>
                          </span>
                        ));
                      })()}
                    </span>
                    <span className="rca-rationale-chevron" aria-hidden>{rationaleDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {rationaleDropdownOpen && (
                    <div className="rca-rationale-dropdown" role="listbox">
                      {rationaleOptions.map((opt) => {
                        const { selected } = parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label));
                        const checked = selected.includes(opt.label);
                        return (
                          <label
                            key={opt.id}
                            role="option"
                            aria-selected={checked}
                            className="rca-rationale-option"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const { selected: sel, other } = parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label));
                                const newSelected = checked ? sel.filter((l) => l !== opt.label) : [...sel, opt.label];
                                setForm((f) => ({ ...f, riskAssessmentRationale: serializeRationale(newSelected, other) }));
                              }}
                            />
                            <span>{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="rca-rationale-other-wrap">
                  <label className="rca-rationale-other-label">{t('rationaleOther')}</label>
                  <textarea
                    value={parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label)).other}
                    onChange={(e) => {
                      const { selected } = parseRationaleValue(form.riskAssessmentRationale, rationaleOptions.map((o) => o.label));
                      setForm((f) => ({ ...f, riskAssessmentRationale: serializeRationale(selected, e.target.value) }));
                    }}
                    className="rca-textarea rca-rationale-other"
                    rows={2}
                    placeholder={t('rationaleOther')}
                  />
                </div>
              </>
            ) : (
              <textarea
                value={form.riskAssessmentRationale}
                onChange={(e) => setForm((f) => ({ ...f, riskAssessmentRationale: e.target.value }))}
                className="rca-textarea"
                rows={2}
                placeholder={t('riskAssessmentRationale')}
              />
            )}
          </div>
          <div className="rca-form-row">
            <label>{t('autocontrolFrequency')}</label>
            <select
              value={form.autocontrolFrequencyCode ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, autocontrolFrequencyCode: e.target.value || null }))}
              className="rca-select"
            >
              <option value="">—</option>
              {RISK_CLASSIFICATION_FREQUENCY_BUCKETS.map((f) => (
                <option key={f.code} value={f.code}>{t(f.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="rca-form-row">
            <label>{t('additionalMitigationCommitments')}</label>
            <textarea
              value={form.additionalMitigationCommitments ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, additionalMitigationCommitments: e.target.value }))}
              className="rca-textarea"
              rows={1}
            />
          </div>
          {saveError && <p className="rca-error-text">{saveError}</p>}
          <div className="rca-form-actions">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Saving…') : t('saveAssignment')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>
              {t('cancel') || 'Cancel'}
            </button>
          </div>
        </div>
  );

  return (
    <div className="rc-assignment-panel">
      <h4 className="rca-title">📋 {t('assignmentSection')}</h4>

      {useModal && modalOpen && editing && (
        <div className="rca-modal-overlay" onClick={() => cancelEdit()}>
          <div className="rca-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rca-modal-header">
              <h3>{t('assignmentSection')}</h3>
              <button type="button" className="rca-modal-close" onClick={cancelEdit} aria-label="Close">×</button>
            </div>
            <div className="rca-modal-body">
              {formContent}
            </div>
          </div>
        </div>
      )}

      {editing && !useModal ? formContent : (
        <>
          {assignment ? (
            <div className="rca-view">
              <div className="rca-row">
                <span className="rca-label">{t('pcFaS')}</span>
                <span className="rca-value">PC={assignment.pc} FA={assignment.fa} S={assignment.s} → {t('score')} {(assignment.pc ?? 0) + (assignment.fa ?? 0) + (assignment.s ?? 0)}</span>
              </div>
              {effective?.level && (
                <div className="rca-row">
                  <span className="rca-label">{t('effectiveLevel')}</span>
                  <span
                    className="rca-badge"
                    style={{ backgroundColor: effective.level.color || '#6b7280', color: '#fff' }}
                  >
                    {effective.level.label}
                  </span>
                </div>
              )}
              <div className="rca-row">
                <span className="rca-label">{t('riskAssessmentRationale')}</span>
                <span className="rca-value">
                  {assignment.riskAssessmentRationale
                    ? (assignment.riskAssessmentRationale.split(RATIONALE_SEPARATOR).map((s) => s.trim()).filter(Boolean).length > 1
                      ? (
                          <ul className="rca-rationale-list-view">
                            {assignment.riskAssessmentRationale.split(RATIONALE_SEPARATOR).map((s) => s.trim()).filter(Boolean).map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        )
                      : assignment.riskAssessmentRationale)
                    : '—'}
                </span>
              </div>
              {assignment.autocontrolFrequencyCode && (
                <div className="rca-row">
                  <span className="rca-label">{t('autocontrolFrequency')}</span>
                  <span className="rca-value">
                    {RISK_CLASSIFICATION_FREQUENCY_BUCKETS.find((f) => f.code === assignment.autocontrolFrequencyCode)
                      ? t(RISK_CLASSIFICATION_FREQUENCY_BUCKETS.find((f) => f.code === assignment.autocontrolFrequencyCode).labelKey)
                      : assignment.autocontrolFrequencyCode}
                  </span>
                </div>
              )}
              <div className="rca-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={startEdit}>Edit</button>
              </div>
            </div>
          ) : effective?.inherited ? (
            <div className="rca-view rca-inherited">
              <p className="rca-inherited-label">{t('inheritedNotReviewed')}</p>
              {effective?.level && (
                <div className="rca-row">
                  <span className="rca-label">{t('effectiveLevel')}</span>
                  <span
                    className="rca-badge"
                    style={{ backgroundColor: effective.level.color || '#6b7280', color: '#fff' }}
                  >
                    {effective.level.label}
                  </span>
                </div>
              )}
              <button type="button" className="btn btn-primary btn-sm" onClick={startEdit}>{t('configureRisk')}</button>
            </div>
          ) : (
            <div className="rca-view rca-empty">
              <p className="rca-muted">{t('notConfigured')}</p>
              <button type="button" className="btn btn-primary btn-sm" onClick={startEdit}>{t('configureRisk')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RiskClassificationAssignmentPanel;
